import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import { useAuthed } from '../hooks/useAuthed';
import { gymApi } from '../api/gymApi';
import type { ExerciseHistory } from '../../../shared/types';

const fmtDate = (secs: number) =>
  new Date(secs * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const round = (n: number | null) => (n == null ? '—' : Math.round(n * 10) / 10);

/** Per-exercise history and personal records. */
export default function ExerciseHistoryPage() {
  const { name } = useParams<{ name: string }>();
  const authed = useAuthed();
  const [data, setData] = useState<ExerciseHistory | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!name) return;
    let cancelled = false;
    gymApi.getExerciseHistory(name, authed)
      .then(d => { if (!cancelled) setData(d); })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load history'); });
    return () => { cancelled = true; };
  }, [name, authed]);

  const best = data?.personalRecords.bestWeight ?? null;

  return (
    <>
      <SiteHeader back={{ label: 'History', to: '/history' }} />
      <main className="page page--narrow">
        <h1>{name}</h1>

        {error && <p className="error">{error}</p>}
        {!data && !error && <p className="muted">Loading…</p>}

        {data && data.sessionCount === 0 && (
          <p className="muted">You haven't logged this exercise yet.</p>
        )}

        {data && data.sessionCount > 0 && (
          <>
            <section className="stat-row">
              <div className="stat">
                <span className="stat-value">{round(data.personalRecords.bestWeight)}</span>
                <span className="stat-label">best weight</span>
              </div>
              <div className="stat">
                <span className="stat-value">{round(data.personalRecords.bestEstimated1Rm)}</span>
                <span className="stat-label">est. 1RM</span>
              </div>
              <div className="stat">
                <span className="stat-value">{data.sessionCount}</span>
                <span className="stat-label">sessions</span>
              </div>
            </section>

            <h2>Sessions</h2>
            {data.history.map(entry => {
              const isPr = best != null && entry.bestWeight != null && entry.bestWeight >= best;
              return (
                <section key={entry.sessionId} className="past-exercise">
                  <h3>
                    {fmtDate(entry.performedAt)}
                    {isPr && <span className="badge badge--pr">PR</span>}
                  </h3>
                  <p className="muted">{entry.dayLabel}</p>
                  <ul className="past-set-list">
                    {entry.sets.filter(s => !s.warmup && s.completed).map(s => (
                      <li key={s.setNumber}>
                        <span className="muted">Set {s.setNumber}</span>
                        <span>{s.weight ?? '—'} × {s.reps ?? '—'}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </>
        )}
      </main>
    </>
  );
}
